import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Download, Loader2, Target } from "lucide-react";
import { workLook } from "./WorkingParcel";
import { IdleReasonDialog } from "./IdleReasonDialog";
import { StreakBadge } from "./TargetCard";
import { Leaderboard } from "./Leaderboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const IDLE = { bg: "#fef3c7", bar: "#f59e0b", text: "#92400e" };
const today = () => new Date(Date.now() + 330 * 60000).toISOString().split("T")[0];
const hh = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:00`;

const dl = async (url, params, name) => {
  try {
    const r = await axios.get(`${API}${url}`, { headers: getAuthHeader(), params, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([r.data]));
    a.download = name;
    a.click();
  } catch { toast.error("Excel download failed"); }
};

const Progress = ({ done, target, testid }) => {
  if (!target) return <span className="text-[11px] text-slate-400" data-testid={testid}>no target</span>;
  const pct = done / target;
  const tone = pct >= 1 ? ["bg-emerald-500", "text-emerald-700"] : pct >= 0.7 ? ["bg-amber-500", "text-amber-700"] : ["bg-rose-500", "text-rose-700"];
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={testid}>
      <span className="w-24 h-2 rounded-full bg-slate-200 overflow-hidden"><span className={`block h-full ${tone[0]}`} style={{ width: `${Math.min(100, pct * 100)}%` }} /></span>
      <span className={`text-xs font-bold ${tone[1]}`}>{done} / {target} parcels</span>
    </span>
  );
};

const Bar = ({ p, from, to, onIdle }) => {
  const look = workLook({ email: p.email, colour: p.colour, at: null });
  const span = to - from;
  return (
    <div className="relative h-9 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden" data-testid={`timeline-bar-${p.email}`}>
      {Array.from({ length: span / 60 + 1 }, (_, i) => (
        <div key={i} className="absolute top-0 bottom-0 border-l border-slate-200/80" style={{ left: `${(i * 60 / span) * 100}%` }} />
      ))}
      {p.blocks.map((b, i) => {
        const left = Math.max(0, (b.start_min - from) / span) * 100;
        const width = Math.max(0.6, Math.min(100 - left, (Math.max(b.minutes, 3) / span) * 100));
        const c = b.idle ? IDLE : { bg: look.chip || look.bg, bar: look.bar, text: look.text };
        return (
          <div key={i} onClick={() => b.idle && onIdle(b)}
            className={`absolute top-1 bottom-1 rounded-md border-l-4 px-1 text-[10px] leading-7 font-semibold truncate ${b.idle ? "cursor-pointer" : ""}`}
            style={{ left: `${left}%`, width: `${width}%`, backgroundColor: c.bg, borderLeftColor: c.bar, color: c.text }}
            title={`${b.start}–${b.end} · ${b.took}${b.idle ? ` (idle${b.reason ? `: ${b.reason}` : " — click to add reason"})` : ""}\n${b.customer} · ${b.code}\nInvoice ${b.invoice_no}`}
            data-testid={`timeline-block-${p.email}-${i}`}>
            {b.idle && b.reason ? `${b.reason} · ` : ""}{b.customer}
          </div>
        );
      })}
    </div>
  );
};

const Person = ({ p, from, to, onIdle }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`timeline-person-${p.email}`}>
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
      <span className="font-semibold text-slate-800">{p.name}</span>
      <StreakBadge r={p} testid={`timeline-badge-${p.email}`} />
      <Progress done={p.parcels} target={p.target} testid={`timeline-progress-${p.email}`} />
      <span className="text-xs text-slate-600">active <b>{p.active}</b></span>
      <span className={`text-xs ${p.idle_blocks ? "text-amber-700" : "text-slate-600"}`}>longest gap <b>{p.longest_gap}</b>{p.idle_blocks ? ` · ${p.idle_blocks} idle` : ""}</span>
    </div>
    <Bar p={p} from={from} to={to} onIdle={onIdle} />
    <div className="flex justify-between text-[10px] text-slate-400 mt-0.5"><span>{hh(from)}</span><span>{hh(to)}</span></div>
    <details className="mt-1">
      <summary className="text-[11px] text-slate-500 cursor-pointer" data-testid={`timeline-list-toggle-${p.email}`}>Parcel list</summary>
      <table className="w-full text-xs mt-1">
        <tbody>
          {p.blocks.map((b, i) => (
            <tr key={i} className={`border-t border-slate-100 ${b.idle ? "bg-amber-50" : ""}`}>
              <td className="px-2 py-1 font-mono text-slate-500 whitespace-nowrap">{b.start}–{b.end}</td>
              <td className="px-2 py-1 font-semibold">{b.customer}</td>
              <td className="px-2 py-1 text-slate-500">{b.code}</td>
              <td className="px-2 py-1">Inv {b.invoice_no}</td>
              <td className={`px-2 py-1 text-right whitespace-nowrap ${b.idle ? "text-amber-700 font-semibold" : ""}`}>
                {b.took}{b.idle ? " · idle" : ""}{b.last ? " (last)" : ""}
                {b.idle && (
                  <button onClick={() => onIdle(b)} className="ml-2 underline decoration-dotted" data-testid={`timeline-reason-${p.email}-${i}`}>
                    {b.reason || "add reason"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  </div>
);

const TargetsDialog = ({ open, onClose, onSaved }) => {
  const [t, setT] = useState(null);
  useEffect(() => {
    if (open) axios.get(`${API}/reports/shift-targets`, { headers: getAuthHeader() }).then(r => setT(r.data)).catch(() => toast.error("Could not load targets"));
  }, [open]);
  const save = async () => {
    try {
      await axios.put(`${API}/reports/shift-targets`, { default: t.default, members: t.members }, { headers: getAuthHeader() });
      toast.success("Daily targets saved"); onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="targets-dialog">
        <DialogHeader><DialogTitle>Daily parcel targets</DialogTitle></DialogHeader>
        {!t ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="space-y-2 text-sm">
            <label className="flex items-center justify-between font-semibold">Default for everyone
              <input type="number" min={1} value={t.default ?? ""} onChange={e => setT({ ...t, default: e.target.value })}
                className="w-24 px-2 py-1 border rounded text-right" data-testid="target-default" placeholder="none" /></label>
            <div className="border-t pt-2 space-y-1.5">
              {t.users.map(u => (
                <label key={u.email} className="flex items-center justify-between gap-2 text-xs">
                  <span className="truncate">{u.name} <span className="text-slate-400">{u.email}</span></span>
                  <input type="number" min={1} value={t.members[u.email.toLowerCase()] ?? ""} placeholder={t.default ? `${t.default}` : "—"}
                    onChange={e => setT({ ...t, members: { ...t.members, [u.email.toLowerCase()]: e.target.value } })}
                    className="w-20 px-2 py-1 border rounded text-right" data-testid={`target-${u.email}`} />
                </label>
              ))}
            </div>
            <button onClick={save} className="w-full mt-2 px-3 py-2 rounded-lg bg-[#2F5496] text-white font-semibold" data-testid="targets-save">Save targets</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const WeekGrid = ({ start, tick }) => {
  const [w, setW] = useState(null);
  useEffect(() => {
    setW(null);
    axios.get(`${API}/reports/work-timeline/week`, { headers: getAuthHeader(), params: { start } })
      .then(r => setW(r.data)).catch(() => setW({ rows: [], days: [] }));
  }, [start, tick]);
  if (!w) return <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!w.rows.length) return <div className="p-6 text-center text-sm text-slate-500" data-testid="week-empty">No parcels were started this week.</div>;
  const shade = (n) => `rgba(47, 84, 150, ${0.06 + 0.5 * (n / (w.max_parcels || 1))})`;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs min-w-[760px]" data-testid="week-grid">
        <thead><tr>
          <th className="text-left px-2 py-1.5 bg-[#DDEBF7] border border-slate-200">Member</th>
          {w.days.map(d => <th key={d} className="px-2 py-1.5 bg-[#DDEBF7] border border-slate-200">{new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" })}</th>)}
          <th className="px-2 py-1.5 bg-[#DDEBF7] border border-slate-200">Week</th>
        </tr></thead>
        <tbody>
          {w.rows.map(r => (
            <tr key={r.email} data-testid={`week-row-${r.email}`}>
              <td className="px-2 py-1.5 border border-slate-200 font-semibold whitespace-nowrap">{r.name}{r.target ? <span className="ml-1 text-[10px] text-slate-400">target {r.target}</span> : null} <StreakBadge r={r} testid={`week-badge-${r.email}`} /></td>
              {w.days.map(d => {
                const v = r.days[d];
                const below = r.target && v && v.parcels < r.target;
                return (
                  <td key={d} className={`px-2 py-1.5 border text-center ${below ? "border-rose-300 ring-1 ring-inset ring-rose-300" : "border-slate-200"}`}
                    style={{ backgroundColor: v ? shade(v.parcels) : undefined }} data-testid={`week-cell-${r.email}-${d}`}>
                    {v ? (<><div className={`font-bold ${below ? "text-rose-700" : "text-slate-800"}`}>{v.parcels}</div><div className="text-[10px] text-slate-600">{v.active}</div></>) : <span className="text-slate-300">—</span>}
                  </td>
                );
              })}
              <td className="px-2 py-1.5 border border-slate-200 text-center bg-slate-50">
                <div className="font-bold">{r.total_parcels}</div><div className="text-[10px] text-slate-500">{r.total_active}</div>
                {r.days_below_target > 0 && <div className="text-[10px] text-rose-600 font-semibold">{r.days_below_target} below</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const WorkTimeline = () => {
  const [view, setView] = useState("day");
  const [date, setDate] = useState(today());
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [idleRef, setIdleRef] = useState(null);
  const [targetsOpen, setTargetsOpen] = useState(false);
  useEffect(() => {
    if (view !== "day") return;
    setD(null);
    axios.get(`${API}/reports/work-timeline`, { headers: getAuthHeader(), params: { date } })
      .then(r => setD(r.data)).catch(e => { toast.error(e.response?.data?.detail || "Could not load timeline"); setD({ people: [] }); });
  }, [date, view, tick]);
  const all = (d?.people || []).flatMap(p => p.blocks);
  const from = Math.min(9 * 60, ...all.map(b => Math.floor(b.start_min / 60) * 60));
  const to = Math.max(21 * 60, ...all.map(b => Math.ceil((b.start_min + b.minutes) / 60) * 60));
  return (
    <div className="p-4 pt-2 space-y-3" data-testid="work-timeline">
      <IdleReasonDialog idleRef={idleRef} onClose={() => setIdleRef(null)} onSaved={() => setTick(t => t + 1)} />
      <TargetsDialog open={targetsOpen} onClose={() => setTargetsOpen(false)} onSaved={() => setTick(t => t + 1)} />
      <div className={`flex flex-wrap items-center gap-2 ${view === "leaderboard" ? "[&>*:not(:first-child)]:hidden" : ""}`}>
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-xs font-semibold">
          {["day", "week", "leaderboard"].map(v => (
            <button key={v} onClick={() => setView(v)} data-testid={`timeline-view-${v}`}
              className={`px-3 py-1.5 ${view === v ? "bg-[#2F5496] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{v === "day" ? "Day" : v === "week" ? "Week" : "Leaderboard"}</button>
          ))}
        </div>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="timeline-date"
          className="px-2 py-1 border border-slate-300 rounded-lg text-sm" />
        <span className="text-xs text-slate-500">{view === "day"
          ? "Each block = a parcel started (first invoice #), until the next parcel. Amber = gap over 30 min — click it to add a reason."
          : "Mon–Sun week containing the picked date. Darker = more parcels; red outline = below daily target."}</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => setTargetsOpen(true)} data-testid="timeline-targets-btn"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50">
            <Target className="w-3.5 h-3.5" /> Targets
          </button>
          <button onClick={() => view === "day" ? dl("/reports/work-timeline/excel", { date }, `Work_Timeline_${date}.xlsx`) : dl("/reports/work-timeline/week/excel", { start: date }, `Work_Week_${date}.xlsx`)}
            data-testid="timeline-excel-btn" className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
        </div>
      </div>
      {view === "leaderboard" ? <Leaderboard />
        : view === "week" ? <WeekGrid start={date} tick={tick} />
        : !d ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        : d.people.length === 0 ? <div className="p-6 text-center text-sm text-slate-500" data-testid="timeline-empty">No parcels were started on this day.</div>
        : d.people.map(p => <Person key={p.email} p={p} from={from} to={to}
            onIdle={(b) => setIdleRef({ email: p.email, name: p.name, invoice_no: b.invoice_no, customer_name: b.customer, date: d.date })} />)}
    </div>
  );
};
